/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { NewVisualizationMenuData } from 'components/common/Layout/Header/NewVisualizationNestedMenu/NewVisualizationMenuData';
import { getDefaultStore } from 'jotai';
import { AuthRoles, dashboardRolesMap, roleVisualizationsMap } from 'jotai/auth';
import { activeDashboardTypeSelector, DashboardType, dashboardVisualizationsMap } from 'jotai/dashboard';
import {
  allVisualizationTypeOptions,
  qualifyingVisualizationTypeOptions,
  restrictedVisualizations,
  VisualizationTypeOption,
} from 'jotai/visualization';
import { authService } from 'services/auth/AuthService';
import isCombinedViewOnlyUser from './isCombinedViewOnlyUser';
import isGMUser from './isGmUser';

const defaultStore = getDefaultStore();

export const isAuthorized = (visualization: VisualizationTypeOption): boolean => {
  if (isProductionHidden(visualization) || isUnauthorizedInCombinedView(visualization) || visualization.isHidden)
    return false;

  if (visualization.type === undefined) return true;

  const activeDashboardType = defaultStore.get(activeDashboardTypeSelector);
  switch (activeDashboardType) {
    case DashboardType.IndyCar:
      return isDashboardAuthorized(visualization) && isUserAuthorized(visualization);
    default:
      return isAuthorizedDefaultVisualization(visualization);
  }
};

const isUserAuthorized = (visualization: VisualizationTypeOption): boolean => {
  const activeDashboardType = defaultStore.get(activeDashboardTypeSelector);
  const dashboardRoles = dashboardRolesMap.get(activeDashboardType) || new Set();
  return (
    !visualization.isHidden && // must not be globally hidden AND EITHER
    [...dashboardRoles].some(
      // the user has some role that authorizes the vis
      (role) =>
        (authService.hasRoleAllRealms(role) || (role === AuthRoles.GM && isGMUser())) &&
        visualization.type &&
        roleVisualizationsMap.get(role)?.has(visualization.type)
    )
  );
};

const isDashboardAuthorized = (visualization: VisualizationTypeOption): boolean => {
  const activeDashboardType = defaultStore.get(activeDashboardTypeSelector);
  const dashboardVisualizations = dashboardVisualizationsMap.get(activeDashboardType) || new Set();
  return !visualization.isHidden && visualization.type !== undefined && dashboardVisualizations.has(visualization.type);
};

/**
 * @param visualization - the visualization to be authorized
 * @returns true for unhidden visualizations, visualizations without a type, and hidden visualizations
 * when the user has at least one role that authorizes the visualization's use.
 */
const isAuthorizedDefaultVisualization = (visualization: VisualizationTypeOption): boolean =>
  !visualization.isHidden && // must not be globally hidden
  visualization.type !== undefined && // must not be a header / types drive authorization
  (!restrictedVisualizations.has(visualization.type) || // must not be in a restricted set
    // or there is some role that the user has that authorizes the vis
    [...roleVisualizationsMap.entries()].some(
      ([role, visualizations]) =>
        (authService.hasRoleAllRealms(role) || (role === AuthRoles.GM && isGMUser())) &&
        visualization.type &&
        visualizations.has(visualization.type)
    ));

const getAuthorizedVisualizations = (visualizationSet: VisualizationTypeOption[]): VisualizationTypeOption[] => {
  const authorizedVisualizations = new Set<VisualizationTypeOption>();
  let lastHeader: VisualizationTypeOption;
  visualizationSet.forEach((item) => {
    if (item.isHeader) {
      lastHeader = item;
    } else if (item.type && isAuthorized(item)) {
      if (lastHeader) {
        authorizedVisualizations.add(lastHeader); // make sure headers are added to the set if they have at least one child item
      }
      authorizedVisualizations.add(item);
    }
  });

  return Array.from(authorizedVisualizations);
};

// TODO have API call that populates hideOnProduction VisualizationTypes
export const isProductionHidden = (visualization: VisualizationTypeOption) =>
  process.env.REACT_APP_ENVIRONMENT.toString() === 'production' && visualization?.isHiddenOnProduction;

// TODO remove with combined view(?)
export const isUnauthorizedInCombinedView = (visualization: VisualizationTypeOption) =>
  !visualization?.combinedViewOnly && isCombinedViewOnlyUser();

export const getAllVisualizationMenuList = () => {
  const visualizationSet =
    defaultStore.get(activeDashboardTypeSelector) === DashboardType.Qualifying
      ? getAuthorizedVisualizations(qualifyingVisualizationTypeOptions)
      : getAuthorizedVisualizations(allVisualizationTypeOptions);

  const menuItemsData: NewVisualizationMenuData = {
    label: 'Add Visual',
    items: [],
  };

  let currHeader = '';
  let labels = allVisualizationTypeOptions.filter((item) => item.isHeader);
  if (labels.length > 0) {
    visualizationSet.forEach((vis) => {
      if (
        // hide from New+ menu if...
        vis.isHidden ||
        isProductionHidden(vis) ||
        isUnauthorizedInCombinedView(vis)
      ) {
        return;
      } else {
        if (vis.isHeader) {
          currHeader = vis.label;
          menuItemsData.items?.push({
            label: vis.label,
            items: [],
          });
        } else {
          if (currHeader.length > 0) {
            menuItemsData.items
              ?.find((data) => data.label === currHeader)
              ?.items?.push({
                label: vis.label,
                type: vis.type,
              });
          } else {
            menuItemsData.items?.push({
              label: vis.label,
              type: vis.type,
            });
          }
        }
      }
    });
  } else {
    visualizationSet.forEach((vis) => {
      menuItemsData.items?.push({
        label: vis.label,
        type: vis.type,
      });
    });
  }
  return menuItemsData;
};